import {
  AUTH_REQUEST,
  AUTH_REQUEST_2FA,
  AUTH_REQUEST_2FA_RENEW,
  AUTH_ERROR,
  AUTH_SUCCESS,
  AUTH_LOGOUT,
  AUTH_REFRESH,
  CHECK_TOKEN
} from '../actions/auth'
import { HIDE_MODAL } from '../actions/modal'
import { USER_SUCCESS } from '../actions/user'
import AuthService from '@/services/AuthService'
import { disconnect as disconnectPusher } from '@/plugins/pusher'
import axios from 'axios'

const state = {
  token: localStorage.getItem('avanti-token') || '',
  status: '',
  refreshing: false
}

const getters = {
  hasToken: state => !!state.token,
  authStatus: state => state.status,
  isRefreshing: state => !!state.refreshing,
  tokenExpireTime: state => {
    if (state.token) {
      try {
        const exp = decodeJwt(state.token).payload.exp * 1000
        return exp
      } catch (e) {
        // Ignore
      }
    }

    return 0
  }
}

const actions = {
  [AUTH_REQUEST]: ({ commit, dispatch }, user) => {
    return new Promise((resolve, reject) => {
      commit(AUTH_REQUEST)

      AuthService.login(user.username, user.password)
        .then(response => {
          if (response.data.success === false && response.data.method === 'sms') {
            // Set user as well
            // commit(USER_2FA, response.data.user_id)
            localStorage.setItem('user-2fa', response.data.user_id)
            localStorage.setItem('expire-2fa', response.data.expire)

            // Success
            resolve(response)
          } else {
            // Save token to local storage
            localStorage.setItem('avanti-token', response.data.token)

            // Add token as auth header
            axios.defaults.headers.common['Authorization'] =
              'Bearer ' + response.data.token

            // Set user as well
            commit(USER_SUCCESS, response.data.user)

            // Success
            commit(AUTH_SUCCESS, response.data.token)
            resolve(response)
          }
        })
        .catch(error => {
          // Remove data 2FA from local storage
          localStorage.removeItem('user-2fa')
          localStorage.removeItem('expire-2fa')

          // Remove token from local storage
          localStorage.removeItem('avanti-token')

          // Remove token from auth header
          delete axios.defaults.headers.common['Authorization']

          // Failed
          commit(AUTH_ERROR)
          reject(error)
        })
    })
  },
  [AUTH_REQUEST_2FA]: ({ commit, dispatch }, result) => {
    return new Promise((resolve, reject) => {
      commit(AUTH_REQUEST_2FA)

      AuthService.verify({'sms_code': result.sms_code, 'user_id': result.user_id})
        .then(response => {
          commit(AUTH_REQUEST)

          // Save token to local storage
          localStorage.setItem('avanti-token', response.data.token)
          localStorage.setItem('avanti-hash2FA', response.data.hash2FA)

          // Add token as auth header
          axios.defaults.headers.common['Authorization'] =
            'Bearer ' + response.data.token

          // Set user as well
          commit(USER_SUCCESS, response.data.user)

          // Success
          commit(AUTH_SUCCESS, response.data.token)
          resolve(response)
        })
        .catch(error => {
          // Remove token from local storage
          localStorage.removeItem('avanti-token')
          localStorage.removeItem('avanti-hash2FA')

          // Remove token from auth header
          delete axios.defaults.headers.common['Authorization']

          // Failed
          commit(AUTH_ERROR)
          reject(error)
        })
    })
  },
  [AUTH_REQUEST_2FA_RENEW]: ({ commit, dispatch }, result) => {
    return new Promise((resolve, reject) => {
      commit(AUTH_REQUEST_2FA_RENEW)

      AuthService.verify({'renew': result.renew, 'user_id': result.user_id})
        .then(response => {
          localStorage.setItem('user-2fa', response.data.user_id)
          localStorage.setItem('expire-2fa', response.data.expire)
          resolve(response)
        })
        .catch(error => {
          // Failed
          reject(error)
        })
    })
  },
  [AUTH_LOGOUT]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      commit(AUTH_LOGOUT)

      // Logout from backend API
      AuthService.logout()

      // Remove data 2FA from local storage
      localStorage.removeItem('user-2fa')
      localStorage.removeItem('expire-2fa')

      // Remove token from local storage
      localStorage.removeItem('avanti-token')

      // Remove token from auth header
      delete axios.defaults.headers.common['Authorization']

      // Close the Pusher socket - otherwise it keeps re-authorizing with a
      // token that no longer exists.
      disconnectPusher()

      dispatch(HIDE_MODAL)

      // Success
      resolve()
    })
  },
  [AUTH_REFRESH]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      try {
        const exp = new Date(decodeJwt(state.token).payload.exp * 1000)
        const now = new Date()
        const sec = Math.round((exp - now) / 1000)

        // When to run the refresh -> when token expires in X hours
        const max = 7200 // 7200 = 2h

        // Only $MAX seconds left?
        if (sec > max) {
          resolve()
        } else {
          AuthService.refresh()
            .then((response) => {
              // Success
              commit(AUTH_REFRESH, response.data.token)
            })
            .catch((error) => {
              console.log('Refresh failed')
              console.log(error)
            })
            .then(() => {
              resolve()
            })
        }
      } catch (err) {
        console.log('Decoding of token failed - ignore')
        console.log(err)
        reject(err)
      }
    })
  },
  [CHECK_TOKEN]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      try {
        const current = localStorage.getItem('avanti-token')

        if (!current) {
          // No current token = Not logged in anymore
          return reject(new Error('No current token'))
        } else if (state.token !== current) {
          // Current token has been updated, so set state-token to the new value
          commit(AUTH_REFRESH, current)
        }

        // Now calc and return the current expire time
        const exp = new Date(decodeJwt(state.token).payload.exp * 1000)
        const now = new Date()
        const sec = Math.round((exp - now) / 1000)

        resolve({ expire: sec })
      } catch (error) {
        return reject(error)
      }

      return reject(new Error('Invalid token'))
    })
  }
}

function decodeJwt (token) {
  const parts = token.split('.')

  if (parts.length !== 3) {
    throw new Error('Invalid JWT token')
  }

  const decodeBase64Url = (str) => {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    )
    return JSON.parse(jsonPayload)
  }

  const header = decodeBase64Url(parts[0])
  const payload = decodeBase64Url(parts[1])

  return {
    header,
    payload,
    signature: parts[2]
  }
}

const mutations = {
  [AUTH_REQUEST]: state => {
    state.status = 'loading'
  },
  [AUTH_REQUEST_2FA]: (state, userId) => {
    state.status = 'loading'
    state.user_id = userId
  },
  [AUTH_REQUEST_2FA_RENEW]: (state, userId) => {
    state.status = 'loading'
    state.user_id = userId
  },
  [AUTH_SUCCESS]: (state, token) => {
    state.status = 'success'
    state.token = token
  },
  [AUTH_ERROR]: state => {
    state.status = 'error'
  },
  [AUTH_LOGOUT]: state => {
    state.token = ''
  },
  [AUTH_REFRESH]: (state, token) => {
    // Save token to local storage
    localStorage.setItem('avanti-token', token)

    // Add token as auth header
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token

    state.status = 'success'
    state.token = token
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/auth.js